import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import './DataTable.scss'

import { compose, branch, mapProps } from 'recompose'
import ReactTable from 'react-table'
import remoteDataHOC from './wrappers/withRemoteData'
import virtualBodyHOC from './wrappers/withVirtualBody'
import mutateRowHOC from './wrappers/withMutateRow'
import filterFormHOC from './wrappers/withFilterForm'
import rowMenuHOC from './wrappers/withRowMenu'
import colFilterHOC from './wrappers/withColFilter'
import searchHOC from './wrappers/withSearch'
import selectRowHOC from './wrappers/withSelectRow'
import remoteSortHOC from './wrappers/withRemoteSort'
import addIconHOC from './wrappers/withAddIcon'
import { LoadingComponent } from './components'
import propsChangeCheck from 'helpers/propsChangeCheck'
import addRowColorTag from './hooks/addRowColorTag'
import ContenHeader from 'components/ContenHeader'

import tableStyle from './DataTable.module.scss'
import { useTranslation } from 'react-i18next'

const DataTable = props => {
	const {
		children, ColFilterBtn, FilterWindow, FilterBtn, cssHeight,
		isInsideContentBlock, withRowColorTag, defaultTagColor,
		label, getTrProps, disabled, disabledRows, noDefaultRowClick,
		onRowClick, style, height, columns, tableActions, tableBodyProps, ...rest
	} = props
	const [t] = useTranslation()

	const formatDataType = (formattingData, dataNeededFormat) => {
		if(typeof dataNeededFormat === 'number') {
			return +formattingData
		}
		if(typeof dataNeededFormat === 'string') {
			return `${formattingData}`
		}
		return formattingData
	}

	const getMoreTrProps = (state, rowInfo) => {
		const definedTrProps = getTrProps ? getTrProps(state, rowInfo) : {}
		// Format types of ids in 'disabledRows' prop to corresponding ones in 'columns' props
		let formattedDisabledRowsId = []
		if(disabledRows) {
			formattedDisabledRowsId = disabledRows.map(id => formatDataType(id, rowInfo.original.id))
		}
		const checkingRowDisabling = (checkingRowId = rowInfo.original.id) => formattedDisabledRowsId.indexOf(checkingRowId) >= 0
		return({
			...definedTrProps,
			...!disabled && {
				onClick: (e) => {
					const isRowDisabled = rowInfo.original ? checkingRowDisabling() : false // False if clicked on 'aggregated' row with no 'rowInfo.original'
					if(noDefaultRowClick || isRowDisabled) {
						e.preventDefault()
					}
					if(isRowDisabled) {
						return
					}
					if(onRowClick) {
						onRowClick(state, rowInfo)
					}
					if(definedTrProps.onClick) {
						definedTrProps.onClick(e)
					}
				}
			},
			className: classNames({
				disabled: disabledRows && checkingRowDisabling(),
				[definedTrProps.className]: definedTrProps.className
			})
		})
	}

	let calcHeight = height
	if(!cssHeight && !calcHeight && rest.data.length > 10) {
		calcHeight = 400
	}
	if(!cssHeight && (!calcHeight || calcHeight < 120) && rest.data.length === 0) {
		calcHeight = 120
	}
	const tableCols = useMemo(() => withRowColorTag ? addRowColorTag(columns, defaultTagColor) : columns, [withRowColorTag, columns, defaultTagColor])
	const customStyles = useMemo(() => ({ height: calcHeight, ...style }), [calcHeight, style])

	return (
		<div className={classNames(
			tableStyle.root,
			{ [tableStyle.insideContentBlock]: isInsideContentBlock })}>
			{(label || ColFilterBtn || FilterBtn || tableActions) &&
				<ContenHeader
					{...label && { title: label }}
					rightCol={<>{ColFilterBtn}{FilterBtn}</>}
					tableActions={tableActions}
				/>
			}
			<div
				className={tableStyle.tableBody}
				{...tableBodyProps}
			>
				<ReactTable
					{...rest}
					columns={tableCols}
					noDataText={t('common.strings.table_no_data')}
					LoadingComponent={LoadingComponent}
					pageSize={rest.data ? rest.data.length : 0}
					showPagination={false}
					style={customStyles}
					getTrProps={getMoreTrProps}
					className={classNames(rest.className, { [tableStyle.disabled]: disabled })}
				/>
				{children}
				{FilterWindow}
			</div>
		</div>
	)
}

DataTable.propTypes = {
	data: PropTypes.array,
	label: PropTypes.node,
	getTrProps: PropTypes.func,
	disabled: PropTypes.bool,
	columns: PropTypes.array.isRequired,
	onRowClick: PropTypes.func,
	disabledRows: PropTypes.array, // Array of 'id' (strings or nums) which rows should be disabled
	noDefaultRowClick: PropTypes.bool,
	onLoadedDataPart: PropTypes.func, // Fired before the new portion of data is rendered by 'VirtualizedTBody' Component
	height: PropTypes.oneOfType([ PropTypes.number, PropTypes.func ]),
	remoteOptions: PropTypes.shape({
		query: PropTypes.object,
		withSort: PropTypes.bool,
		withFilter: PropTypes.shape({
			FilterComponent: PropTypes.element,
			baseFilter: PropTypes.object, // Filter params which can't be cleared
			filterValues: PropTypes.object, // Filter params on mount
			onFilter: PropTypes.func, // Fires on submit filter form. Recieves current filter params
			onUnmount: PropTypes.func, // Recieves current filter params
			width: PropTypes.number, // Value in percent
			height: PropTypes.number // Value in percent
		}),
		onCompleted: PropTypes.func, // Fires once, when query successfully completes
		refreshData: PropTypes.bool, // Fires refetch query with previous variables defined in 'remoteOptions.query' if true
		onDataRefreshed: PropTypes.func // Fires after 'remoteOptions.query' called by 'remoteOptions.refreshData' was completed
	})
}

export default compose(
	propsChangeCheck(['data', 'columns', 'match', 'remoteOptions']),
	mapProps(props => {
		if(typeof props.columns === 'function') {
			const [t] = useTranslation()
			return {
				...props,
				columns: props.columns(t)
			}
		}
		return props
	}),
	branch(
		({ rowMenu }) => !!rowMenu && Object.keys(rowMenu).length,
		rowMenuHOC
	),
	branch(
		({ withSearch }) => withSearch,
		searchHOC
	),
	branch(
		({ mutateRow }) => !!mutateRow,
		mutateRowHOC
	),
	branch(
		({ withColFilter }) => withColFilter,
		colFilterHOC
	),
	branch(
		({ onAddRecord }) => !!onAddRecord,
		addIconHOC
	),
	branch(
		({ remoteOptions = {} }) => remoteOptions.withFilter && remoteOptions.withFilter.FilterComponent,
		filterFormHOC
	),
	branch(
		({ remoteOptions = {} }) => remoteOptions.withSort,
		remoteSortHOC
	),
	branch(
		({ remoteOptions }) => !!remoteOptions,
		remoteDataHOC
	),
	branch(
		({ selectRow }) => selectRow,
		selectRowHOC
	),
	branch(
		({ withVirtualBody }) => withVirtualBody,
		virtualBodyHOC
	)
)(DataTable)
